import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaChartLine, FaSyncAlt } from 'react-icons/fa';

import Message from '../../components/layout/Message';
import Meta from '../../components/layout/Meta';
import SalesReportBreakdowns from '../../components/admin/salesReport/SalesReportBreakdowns';
import SalesReportChart from '../../components/admin/salesReport/SalesReportChart';
import SalesReportFilters from '../../components/admin/salesReport/SalesReportFilters';
import SalesReportOrdersTable from '../../components/admin/salesReport/SalesReportOrdersTable';
import SalesReportProductsTable from '../../components/admin/salesReport/SalesReportProductsTable';
import SalesReportSummary, { SalesReportSkeleton } from '../../components/admin/salesReport/SalesReportSummary';
import {
    useGetSalesReportFiltersQuery,
    useGetSalesReportOrdersQuery,
    useGetSalesReportProductsQuery,
    useGetSalesReportQuery,
} from '../../slices/salesReportApiSlice';
import {
    REPORT_TABS,
    activeOrderSort,
    activeProductSort,
    basisLabel,
    buildFilterChips,
    createDefaultFilters,
    currentMonthRange,
    formatDayLabel,
    getQueryErrorMessage,
    readSalesReportSearch,
    salesReportFormKey,
    sanitizeDraft,
    toReportQuery,
    toSearchParams,
    toggleSort,
    validateSalesReportDraft,
} from '../../utils/salesReportUtils';
import '../../assets/styles/sales.report.css';

const useSettledQuery = (queryState) => {
    const [settled, setSettled] = useState(null);

    useEffect(() => {
        if (queryState.data) {
            setSettled(queryState.data);
        }
    }, [queryState.data]);

    const data = queryState.data || (queryState.isFetching ? settled : null);

    return {
        data,
        isFirstLoad: !data && queryState.isFetching,
        isUpdating: Boolean(data) && queryState.isFetching,
        error: queryState.isFetching ? null : queryState.error,
    };
};

const SalesReportScreen = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const searchKey = searchParams.toString();
    const applied = useMemo(() => readSalesReportSearch(new URLSearchParams(searchKey)), [searchKey]);
    const formKey = salesReportFormKey(applied);
    const appliedRef = useRef(applied);
    appliedRef.current = applied;

    const [draft, setDraft] = useState(() => {
        const initial = readSalesReportSearch(new URLSearchParams(window.location.search));
        return initial.startDate && initial.endDate ? initial : { ...initial, ...currentMonthRange() };
    });
    const [draftErrors, setDraftErrors] = useState([]);
    const activeCompany = useSelector((state) => state.admin.activeCompany);

    useEffect(() => {
        if (!appliedRef.current.startDate || !appliedRef.current.endDate) return;
        setDraft(appliedRef.current);
        setDraftErrors([]);
    }, [formKey]);

    useEffect(() => {
        if (applied.startDate && applied.endDate) return;
        const next = { ...appliedRef.current, ...currentMonthRange() };
        setSearchParams(toSearchParams(next), { replace: true });
    }, [applied.startDate, applied.endDate, setSearchParams]);

    const ready = Boolean(applied.startDate && applied.endDate);
    const appliedErrors = ready ? validateSalesReportDraft(applied) : [];
    const canQuery = ready && appliedErrors.length === 0;
    const summaryArgs = useMemo(() => toReportQuery(applied, 'summary'), [applied]);
    const ordersArgs = useMemo(() => toReportQuery(applied, 'orders'), [applied]);
    const productsArgs = useMemo(() => toReportQuery(applied, 'products'), [applied]);

    const filtersQuery = useGetSalesReportFiltersQuery();
    const summaryQuery = useGetSalesReportQuery(summaryArgs, { skip: !canQuery });
    const ordersQuery = useGetSalesReportOrdersQuery(ordersArgs, {
        skip: !canQuery || applied.tab !== 'pedidos',
    });
    const productsQuery = useGetSalesReportProductsQuery(productsArgs, {
        skip: !canQuery || applied.tab !== 'productos',
    });

    const summary = useSettledQuery(summaryQuery);
    const orders = useSettledQuery(ordersQuery);
    const products = useSettledQuery(productsQuery);
    const companies = filtersQuery.data?.companies || [];
    const chips = buildFilterChips(applied, companies);
    const errors = draftErrors.length ? draftErrors : appliedErrors;
    const summaryError = summary.error ? getQueryErrorMessage(summary.error) : '';
    const isUpdating = summary.isUpdating || orders.isUpdating || products.isUpdating;
    const hasProductFilter = Boolean((applied.productId || '').trim() || (applied.contificoCode || '').trim());

    const commit = (nextFilters, { replace = false } = {}) => {
        setDraftErrors([]);
        setSearchParams(toSearchParams(nextFilters), { replace });
    };

    const commitFilters = (nextFilters, options) => {
        const normalized = sanitizeDraft({
            ...nextFilters,
            page: 1,
            productPage: 1,
        });
        const nextErrors = validateSalesReportDraft(normalized);
        setDraft(normalized);
        setDraftErrors(nextErrors);
        if (nextErrors.length) return;
        commit(normalized, options);
    };

    const keepListState = (source = applied) => ({
        tab: source.tab,
        sortBy: source.sortBy,
        sortOrder: source.sortOrder,
        productSortBy: source.productSortBy,
        pageSize: source.pageSize,
    });

    const rangeLabel =
        applied.startDate && applied.endDate
            ? `${formatDayLabel(applied.startDate)} – ${formatDayLabel(applied.endDate)}`
            : '';

    return (
        <div className="sales-report">
            <Meta title="Reportes de ventas" description="Reporte de ventas para administradores" />
            <div className="admin-header">
                <div>
                    <h1 className="admin-title responsive-title">
                        <FaChartLine /> Reportes de ventas
                    </h1>
                    <p className="sr-subtitle">
                        {rangeLabel ? `${rangeLabel} · ` : ''}
                        {basisLabel(applied.basis)} · America/Guayaquil
                        {applied.ruc.length ? '' : ' · Todas las empresas que puedes ver'}
                    </p>
                </div>
                <div className="sr-toolbar">
                    <button
                        type="button"
                        className="sr-btn sr-btn-ghost"
                        onClick={() => {
                            if (!canQuery) return;
                            summaryQuery.refetch();
                            if (applied.tab === 'pedidos') ordersQuery.refetch();
                            if (applied.tab === 'productos') productsQuery.refetch();
                        }}
                        disabled={!canQuery}
                    >
                        <FaSyncAlt className={isUpdating ? 'sr-spin' : ''} /> Actualizar
                    </button>
                </div>
            </div>

            {isUpdating && <div className="sr-progress" role="progressbar" aria-label="Actualizando reporte" />}

            <SalesReportFilters
                draft={draft}
                companies={companies}
                paymentGroups={filtersQuery.data?.paymentMethodGroups || []}
                activeCompanyRuc={activeCompany?.ruc || ''}
                errors={errors}
                forceAdvanced={errors.length > 0}
                onChange={setDraft}
                onApply={() => commitFilters({ ...draft, ...keepListState() })}
                onPreset={(range) => commitFilters({ ...draft, ...range, ...keepListState() })}
                onReset={() => commitFilters({ ...createDefaultFilters(), tab: applied.tab }, { replace: true })}
                onUseActiveCompany={() => {
                    if (!activeCompany?.ruc) return;
                    commitFilters({ ...draft, ...keepListState(), ruc: [activeCompany.ruc] });
                }}
            />

            {!!chips.length && (
                <div className="sr-chips" aria-label="Filtros activos">
                    {chips.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            className="sr-chip"
                            onClick={() => commitFilters(item.clear(applied))}
                        >
                            {item.label}
                            <span aria-hidden="true">×</span>
                            <span className="visually-hidden">Quitar filtro</span>
                        </button>
                    ))}
                </div>
            )}

            {filtersQuery.isError && (
                <p className="sr-hint">No se pudieron cargar las listas de filtros. Puedes consultar el reporte igual.</p>
            )}

            {summaryError && <Message variant="danger">{summaryError}</Message>}
            {!summaryError && orders.error && applied.tab === 'pedidos' && (
                <Message variant="danger">{getQueryErrorMessage(orders.error)}</Message>
            )}
            {!summaryError && products.error && applied.tab === 'productos' && (
                <Message variant="danger">{getQueryErrorMessage(products.error)}</Message>
            )}

            <div className="sr-tabs" role="tablist" aria-label="Vista del reporte">
                {REPORT_TABS.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={applied.tab === tab.id}
                        className={`sr-tab${applied.tab === tab.id ? ' is-active' : ''}`}
                        onClick={() => commit({ ...applied, tab: tab.id })}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {applied.tab === 'resumen' && summary.isFirstLoad && <SalesReportSkeleton />}

            {applied.tab === 'resumen' && summary.data && (
                <div className={summary.isUpdating ? 'sr-updating' : undefined}>
                    {summary.data.summary?.orderCount === 0 && (
                        <p className="sr-panel sr-note">No hay ventas reconocidas en este periodo.</p>
                    )}
                    <SalesReportSummary
                        summary={summary.data.summary}
                        comparison={summary.data.comparison}
                        basis={applied.basis}
                        hasProductFilter={hasProductFilter}
                        generatedAt={summary.data.generatedAt}
                    />
                    <SalesReportChart series={summary.data.series || []} groupBy={applied.groupBy} />
                    <SalesReportBreakdowns
                        breakdowns={summary.data.breakdowns || {}}
                        basis={applied.basis}
                        onSelect={(patch) => commitFilters({ ...applied, ...patch })}
                    />
                </div>
            )}

            {applied.tab === 'pedidos' && orders.isFirstLoad && <SalesReportSkeleton />}
            {applied.tab === 'pedidos' && orders.data && (
                <SalesReportOrdersTable
                    orders={orders.data?.orders || []}
                    total={orders.data?.total || 0}
                    page={orders.data?.page || applied.page}
                    pages={orders.data?.pages || 0}
                    pageSize={applied.pageSize}
                    sortBy={applied.sortBy}
                    sortOrder={applied.sortOrder}
                    basis={applied.basis}
                    isUpdating={orders.isUpdating}
                    onPage={(page) => commit({ ...applied, page })}
                    onPageSize={(pageSize) => commit({ ...applied, pageSize, page: 1, productPage: 1 })}
                    onSort={(field) => {
                        const next = toggleSort(activeOrderSort(applied), field, applied.sortOrder);
                        commit({ ...applied, sortBy: next.field, sortOrder: next.sortOrder, page: 1 });
                    }}
                />
            )}

            {applied.tab === 'productos' && products.isFirstLoad && <SalesReportSkeleton />}
            {applied.tab === 'productos' && products.data && (
                <SalesReportProductsTable
                    products={products.data?.products || []}
                    totals={products.data?.totals}
                    total={products.data?.total || 0}
                    page={products.data?.page || applied.productPage}
                    pages={products.data?.pages || 0}
                    pageSize={applied.pageSize}
                    productSortBy={applied.productSortBy}
                    sortOrder={applied.sortOrder}
                    isUpdating={products.isUpdating}
                    onPage={(productPage) => commit({ ...applied, productPage })}
                    onPageSize={(pageSize) => commit({ ...applied, pageSize, page: 1, productPage: 1 })}
                    onSort={(field) => {
                        const next = toggleSort(activeProductSort(applied), field, applied.sortOrder);
                        commit({
                            ...applied,
                            productSortBy: next.field,
                            sortOrder: next.sortOrder,
                            productPage: 1,
                        });
                    }}
                    onShowOrders={(productId) =>
                        commitFilters({ ...applied, productId: String(productId), tab: 'pedidos' })
                    }
                />
            )}
        </div>
    );
};

export default SalesReportScreen;
